/** Shared NPC dialog header (south portrait + title) and case-file transcript typography. */

import Phaser from "phaser";

import {
  npcDirectionTextureKey,
  npcRotationTexturePathRotations,
} from "../lib/asset-mapper.js";

import { type DialogShellMetrics, isMobileNpcOverlay } from "./dialog-layout.js";
import { UI_FONT, UI_TEXT } from "./ui-theme.js";

/** Matches `#narrative-body` in `client/index.html`. */
export const NARRATIVE_BODY_COLOR = "#d4d8e4";
export const NARRATIVE_TITLE_COLOR = "#d0e4ff";
/** ~line-height 1.55 at 16px body (Phaser lineSpacing is extra px between lines). */
export const CASE_LINE_SPACING = 9;
export const HEADER_TITLE_TOP = 20;
export const HEADER_TRANSCRIPT_GAP = 14;

export function portraitDisplayPx(vw: number): number {
  return isMobileNpcOverlay(vw) ? 64 : 72;
}

export function caseTitleStyle(fontSize: number): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontFamily: UI_FONT,
    fontSize: `${fontSize}px`,
    color: NARRATIVE_TITLE_COLOR,
    fontStyle: "600",
    align: "left",
  };
}

export function caseNpcLineStyle(fontSize: number): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontFamily: UI_FONT,
    fontSize: `${fontSize}px`,
    color: NARRATIVE_BODY_COLOR,
    fontStyle: "400",
    lineSpacing: CASE_LINE_SPACING,
  };
}

export function casePlayerLineStyle(fontSize: number): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontFamily: UI_FONT,
    fontSize: `${fontSize}px`,
    color: UI_TEXT.player,
    fontStyle: "600",
    lineSpacing: CASE_LINE_SPACING,
  };
}

export type NpcHeaderMetrics = {
  contentLeft: number;
  headerBottom: number;
  portraitBottom: number;
  portraitW: number;
  titleX: number;
  titleWrapW: number;
};

export function southPortraitTextureKey(
  scene: Phaser.Scene,
  npcSkinId: string
): string | null {
  const key = npcDirectionTextureKey(npcSkinId, "south");
  return scene.textures.exists(key) ? key : null;
}

/** Queue south rotation art if missing; call `onLoaded` after load completes. */
export function queueSouthPortraitIfMissing(
  scene: Phaser.Scene,
  npcSkinId: string,
  onLoaded?: () => void
): void {
  const key = npcDirectionTextureKey(npcSkinId, "south");
  if (scene.textures.exists(key)) return;
  const loader = scene.load;
  if (loader.keyExists(key)) return;
  loader.image(key, npcRotationTexturePathRotations(npcSkinId, "south"));
  if (onLoaded) {
    loader.once(Phaser.Loader.Events.COMPLETE, onLoaded);
  }
  if (!loader.isLoading()) loader.start();
}

export function measureNpcHeader(
  scene: Phaser.Scene,
  shell: DialogShellMetrics,
  title: string,
  portraitKey: string | null
): NpcHeaderMetrics {
  const contentLeft = shell.panelCx - shell.innerW * 0.5;
  const portraitW = portraitKey ? portraitDisplayPx(shell.vw) : 0;
  const titleX = portraitKey ? contentLeft + portraitW + 12 : contentLeft;
  const titleWrapW = portraitKey
    ? Math.max(120, shell.innerW - portraitW - 12)
    : shell.innerW;

  const titleProbe = scene.add.text(0, 0, title, {
    ...caseTitleStyle(shell.titleFont),
    wordWrap: { width: titleWrapW },
  });
  const titleH = titleProbe.height;
  titleProbe.destroy();

  let portraitH = 0;
  if (portraitKey && scene.textures.exists(portraitKey)) {
    const tex = scene.textures.get(portraitKey);
    const src = tex.getSourceImage() as { width?: number; height?: number };
    const iw = src.width ?? portraitW;
    const ih = src.height ?? portraitW;
    portraitH = (portraitW / Math.max(1, iw)) * ih;
  } else if (portraitKey) {
    portraitH = portraitW;
  }

  const titleY = shell.panelTop + HEADER_TITLE_TOP;
  const portraitCenterY = titleY + Math.max(18, portraitH * 0.5);
  const portraitBottom = portraitKey ? portraitCenterY + portraitH * 0.5 : titleY;
  const titleBottom = titleY + titleH;
  const headerBottom = Math.max(
    titleBottom + 10,
    portraitKey ? portraitBottom + HEADER_TRANSCRIPT_GAP : titleBottom + HEADER_TRANSCRIPT_GAP
  );

  return {
    contentLeft,
    headerBottom,
    portraitBottom,
    portraitW,
    titleX,
    titleWrapW,
  };
}

export type NpcHeaderObjects = {
  metrics: NpcHeaderMetrics;
  title: Phaser.GameObjects.Text;
  portrait: Phaser.GameObjects.Image | null;
};

function portraitDisplayHeight(scene: Phaser.Scene, key: string, displayW: number): number {
  const frame = scene.textures.getFrame(key);
  const iw = Math.max(1, frame.width);
  const ih = Math.max(1, frame.height);
  return (displayW / iw) * ih;
}

export function createNpcHeader(
  scene: Phaser.Scene,
  shell: DialogShellMetrics,
  depth: number,
  title: string,
  portraitKey: string | null
): NpcHeaderObjects {
  const metrics = measureNpcHeader(scene, shell, title, portraitKey);
  const titleY = shell.panelTop + HEADER_TITLE_TOP;

  let portrait: Phaser.GameObjects.Image | null = null;
  if (portraitKey) {
    const displayH = portraitDisplayHeight(scene, portraitKey, metrics.portraitW);
    const portraitCenterY = titleY + Math.max(18, displayH * 0.5);
    portrait = scene.add
      .image(metrics.contentLeft, portraitCenterY, portraitKey)
      .setOrigin(0, 0.5)
      .setDisplaySize(metrics.portraitW, displayH)
      .setScrollFactor(0)
      .setDepth(depth);
  }

  const titleText = scene.add
    .text(metrics.titleX, titleY, title, {
      ...caseTitleStyle(shell.titleFont),
      wordWrap: { width: metrics.titleWrapW },
    })
    .setOrigin(0, 0)
    .setScrollFactor(0)
    .setDepth(depth);

  return { metrics, title: titleText, portrait };
}

export function positionNpcHeader(
  scene: Phaser.Scene,
  shell: DialogShellMetrics,
  header: NpcHeaderObjects,
  title: string,
  portraitKey: string | null
): NpcHeaderMetrics {
  const metrics = measureNpcHeader(scene, shell, title, portraitKey);
  const titleY = shell.panelTop + HEADER_TITLE_TOP;
  header.title.setPosition(metrics.titleX, titleY);
  header.title.setText(title);
  header.title.setWordWrapWidth(metrics.titleWrapW, true);
  if (header.portrait && portraitKey) {
    const displayH = portraitDisplayHeight(scene, portraitKey, metrics.portraitW);
    const portraitCenterY = titleY + Math.max(18, displayH * 0.5);
    header.portrait
      .setTexture(portraitKey)
      .setPosition(metrics.contentLeft, portraitCenterY)
      .setDisplaySize(metrics.portraitW, displayH)
      .setVisible(true);
  } else if (header.portrait) {
    header.portrait.setVisible(false);
  }
  header.metrics = metrics;
  return metrics;
}
